import { Component } from '@theme/component';

/**
 * Landing fade slider
 *
 * Cross-fades its slides on an interval. With a single slide it does nothing at all,
 * so the common case costs no timers and no work.
 *
 * Slides are this element's direct children: they are stock `image` theme blocks, so they
 * cannot carry a `ref` attribute of their own.
 *
 * @extends {Component<{}>}
 */
class LandingFadeSlider extends Component {
  /** @type {number | undefined} */
  #timer;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#slides.length < 2) return;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference);

    this.#setActive(0);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#start();
        else this.#stop();
      }
    });

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#reducedMotion?.removeEventListener('change', this.#handleMotionPreference);
    this.#reducedMotion = undefined;
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.children).filter((el) => el instanceof HTMLElement));
  }

  get #interval() {
    const seconds = Number(this.dataset.autoplaySpeed);

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 4000;
  }

  #handleMotionPreference = () => {
    if (this.#reducedMotion?.matches) {
      this.#stop();
      this.#setActive(0);
    } else {
      this.#start();
    }
  };

  #start() {
    if (this.#timer || this.#reducedMotion?.matches) return;

    this.#timer = window.setInterval(() => this.#advance(), this.#interval);
  }

  #stop() {
    if (!this.#timer) return;

    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #advance() {
    this.#setActive((this.#index + 1) % this.#slides.length);
  }

  /** @param {number} next */
  #setActive(next) {
    this.#index = next;

    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('landing-fade-slider__slide--active', i === next);
    });
  }
}

if (!customElements.get('landing-fade-slider')) {
  customElements.define('landing-fade-slider', LandingFadeSlider);
}
